import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, startWith, switchMap } from 'rxjs';
import {
    IamOrganizationsApiServiceObservable,
    ImpersonateV2Request,
    ImpersonateV2Response,
} from '@kempoweroyj/ngx-iam-organizations-api-client';
import { IamUsersApiServiceObservable, UserTenancyAccessObject } from '@kempoweroyj/ngx-iam-users-api-client';
import {
    OrganizationSearchResponse,
    OrganizationSearchResult,
    SearchApiServiceObservable,
} from '@kempoweroyj/ngx-search-api-client';
import { AuthenticationService } from '@kempower-nx-workspace/ce-authentication';
import { SimplifiedOrganizationDetails } from '../../models/simplified-organization-details.model';

export type GetMyTenancyAccessWithName = UserTenancyAccessObject & { name: string };

export type ImpersonationResponse = DataResponse | ErrorResponse | StatusResponse | StatusErrorResponse | null;

interface DataResponse {
    data: string;
    error?: string;
    status?: string;
}

interface ErrorResponse {
    error: ImpersonateV2Response;
    data?: Record<string, unknown>;
    status?: string;
}

interface StatusResponse {
    status: string;
    error?: string;
    data?: Record<string, unknown>;
}

interface StatusErrorResponse {
    status: string;
    error: HttpErrorResponse | undefined;
    data?: Record<string, unknown>;
}
@Injectable()
export class ImpersonationService {
    iamOrganizationsApiService$ = inject(IamOrganizationsApiServiceObservable);
    iamUsersApiServiceObservable = inject(IamUsersApiServiceObservable);
    iamOrganizationsApiServiceObservable = inject(IamOrganizationsApiServiceObservable);
    authService = inject(AuthenticationService);
    searchApiService = inject(SearchApiServiceObservable);

    /**
     * Initiates an impersonation session using the provided tenant/user information.
     *
     * @param data - The impersonation request payload, including the target tenant and optional user context.
     * @returns An observable emitting the impersonation response, which includes a valid auth token.
     *
     *  Behavior:
     * - Sends a request to the impersonation V2 endpoint.
     * - If a token is present in the response:
     *    - Logs in the user using the token via the authentication service.
     *    - Returns the full response.
     * - Throws an error if the token is missing from the response.
     */
    updateImpersonation$(data: ImpersonateV2Request): Observable<ImpersonateV2Response> {
        return this.iamOrganizationsApiService$.impersonateV2(data).pipe(
            map((response) => {
                if (response?.token) {
                    this.authService.setTokenInStorage(response.token);
                    return response;
                } else {
                    throw new Error('Token not found in the response');
                }
            })
        );
    }

    /**
     * Retrieves the tenancy access list for the currently authenticated user.
     *
     * @returns An observable emitting an array of tenancy access objects,
     *          each enriched with a `name` field for UI usage.
     *
     * - Calls the IAM Users API to get the tenancy access mapping.
     * - Transforms the result from an object map to an array.
     * - Adds a `name` property based on `targetTenantName` for each item.
     */

    getTenacyAccess$(): Observable<GetMyTenancyAccessWithName[]> {
        return this.iamUsersApiServiceObservable.getMyTenancyAccess().pipe(
            map((data) => {
                const values = Object.values(data).map((item) => ({
                    ...item,
                    name: item.targetTenantName,
                }));

                return values;
            })
        );
    }

    /**
     * Retrieves a list of simplified organizations, either in impersonation mode or regular mode.
     *
     * @param isImpersonating - A boolean flag indicating whether the current user is impersonating another tenant.
     * @param originalTenantId - The tenant ID to use when impersonating. Required only if `isImpersonating` is `true`.
     *
     * @returns An observable emitting an array of `SimplifiedOrganizationDetails`.
     *
     * 🔍 Logic:
     * - If `isImpersonating` is true:
     *    - Calls the impersonation endpoint with the `originalTenantId` to fetch a token.
     *    - Sets the token as an `Authorization` header for the search request.
     * - If not impersonating:
     *    - Uses default headers (no token).
     * - Performs an organization search with the `minimized` flag set to `true`.
     * - Transforms the search response to extract a list of `RelOrganization`.
     * - Catches and logs any errors, returning an empty list on failure.
     */
    getOrganisationList$(
        isImpersonating: boolean,
        originalTenantId: string
    ): Observable<SimplifiedOrganizationDetails[]> {
        const orgSearchOptions$ = isImpersonating
            ? this.iamOrganizationsApiServiceObservable.impersonate({ tenantId: originalTenantId }).pipe(
                  map((res) => ({
                      headers: {
                          Authorization: `Bearer ${res.token}`,
                      },
                  }))
              )
            : of({});

        return orgSearchOptions$.pipe(
            switchMap((options) => this.searchApiService.organizationSearch({ minimized: true }, options)),

            map((response: OrganizationSearchResponse) => {
                const results = response.results as OrganizationSearchResult[]; // Only if you're sure
                return results.map((res) => {
                    return res.organization as SimplifiedOrganizationDetails;
                });
            }),
            catchError((err) => {
                console.error('org search failed', err);
                return of([]); // Return an empty list on error
            })
        );
    }

    /**
     * Retrieves the name of the current organization for the authenticated user.
     *
     * @returns An observable that emits the organization's name as a string.
     *          Emits an empty string if the name is not found or data is invalid.
     *
     * Behavior:
     * - Calls the organization API with `minimized: true` to get a lightweight response.
     * - Extracts the `name` field from the response if available.
     * - Defaults to an empty string if `name` is missing or the response is malformed.
     * - Emits an initial value of `''` immediately before the API response arrives.
     */
    getOrganisationName$(): Observable<string> {
        return this.iamOrganizationsApiServiceObservable.getMyOrganization({ minimized: true }).pipe(
            map((data) => {
                if (typeof data === 'object' && data !== null && 'name' in data) {
                    return data.name ?? '';
                }
                return '';
            }),
            startWith('')
        );
    }
}
